/**
 * Client-side entry point.
 *
 * Detects whether SSR content is present:
 * - If SSR content exists: uses hydrateRoot for efficient hydration
 * - If no SSR content (dev mode): uses createRoot for client-side rendering
 */
import { StrictMode } from "react";
import GoogleRecaptcha from "@moovfinancial/common/utils/GoogleRecaptcha";
import { isDevelopmentExtension } from "@moovfinancial/common/utils/env";
import { loadAfterTelemetry } from "@moovfinancial/common/utils/startTelemetry";
import { createRoot, hydrateRoot } from "react-dom/client";
import "./globals.scss";

GoogleRecaptcha.init();

// eslint-disable-next-line no-console
console.log(`ℹ️ Payment Links: %cv${__APP_VERSION__}`, "font-weight: bold");

const rootElement = document.getElementById("root") as HTMLElement;

const hasSSRContent =
  rootElement.children.length > 0 && !rootElement.innerHTML.includes("<!--SSR_OUTLET-->");

const loadApp = async () => {
  if (import.meta.env.DEV && !hasSSRContent && !isDevelopmentExtension()) {
    const { startMockWorker } = await import("./dev/mocks/browser");
    await startMockWorker();
    return import("./dev/DevHarness");
  }
  return import("./App");
};

void loadAfterTelemetry("payment-links", __APP_VERSION__, loadApp)
  .then(({ default: App }) => {
    if (hasSSRContent) {
      hydrateRoot(
        rootElement,
        <StrictMode>
          <App />
        </StrictMode>
      );
    } else {
      createRoot(rootElement).render(
        <StrictMode>
          <App />
        </StrictMode>
      );
    }
  })
  .catch((error: unknown) => {
    // eslint-disable-next-line no-console
    console.error("Failed to start payment-links", error);
  });
